import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { TagSearch } from '@components/core-ui/Tag/variations/TagSearch';
import { TagSearchActive } from '@components/core-ui/Tag/variations/TagSearchActive';
import { ReactNode, useMemo, useState } from 'react';
import CloseIcon from '@components/icons/Close.svg';
import SearchIcon from '@components/icons/Search.svg';
import { Input } from '@components/core-ui/form-elements/Input';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { createFetchOrganizationSearchQueryFn } from 'app/queries/organizations/createFetchOrganizationSearchQueryFn';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';

import { JobsList } from './components/Jobs';
import { UsersList } from './components/Users';
import { WorkspacesList } from './components/Workspaces';

import { Wrapper } from './styles/Wrapper';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';

const SEARCH_DEBOUNCE_MS = 300;

function SearchScopeChip({
  selected,
  onClick,
  className,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return selected ? (
    <TagSearchActive className={className} onClick={onClick}>
      {children}
    </TagSearchActive>
  ) : (
    <TagSearch className={className} onClick={onClick}>
      {children}
    </TagSearch>
  );
}

export const SearchModal = () => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const canManageMembers = !!organization?.permissions?.manageMembers;

  const searchQuery = useQuery({
    queryKey: organizationsKeys.search(organization?.id ?? '', debouncedSearch),
    queryFn: createFetchOrganizationSearchQueryFn(call),
    enabled: debouncedSearch.length > 0 && !isSearchSettling,
    placeholderData: keepPreviousData,
  });
  useNotifyQueryError(searchQuery.error);

  const jobs = searchQuery.data?.jobs ?? [];
  const users = searchQuery.data?.users ?? [];
  const workspaces = useMemo(
    () =>
      debouncedSearch.length > 0
        ? (organization?.workspaces ?? []).filter((ws) => ws.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
        : [],
    [organization?.workspaces, debouncedSearch],
  );
  const loading = search.length > 0 && (searchQuery.isFetching || isSearchSettling);

  const checkTag = (tag: string) => {
    if (tags.includes(tag)) {
      setTags([...tags.filter((t) => t !== tag)]);
    } else {
      setTags([...tags, tag]);
    }
  };

  return (
    <Wrapper>
      <div className="head">
        <SearchIcon className="search-icon" />
        <Input value={search} className="input" onChange={(e) => setSearch(e.target.value)} label="Search" />
        <Button className="clear-button" onClick={() => setSearch('')}>
          Clear
        </Button>
        <IconButtonClean className="close-button" onClick={() => closeModal()}>
          <CloseIcon />
        </IconButtonClean>
      </div>
      <div className="head-divider" />
      <div className="tags">
        <h4>{"I'm searching for:"}</h4>
        <SearchScopeChip selected={tags.length === 0} className="max-w-[150px]" onClick={() => setTags([])}>
          All
        </SearchScopeChip>
        <SearchScopeChip selected={tags.includes('job')} className="max-w-[150px]" onClick={() => checkTag('job')}>
          Job
        </SearchScopeChip>
        {canManageMembers ? (
          <SearchScopeChip selected={tags.includes('user')} className="max-w-[150px]" onClick={() => checkTag('user')}>
            User
          </SearchScopeChip>
        ) : null}
        <SearchScopeChip
          selected={tags.includes('workspace')}
          className="max-w-[150px]"
          onClick={() => checkTag('workspace')}
        >
          Workspace
        </SearchScopeChip>
      </div>
      <div className="results">
        {loading ? <AbsoluteSpinner /> : null}
        {search.length > 0 ? (
          <>
            {jobs.length > 0 && (!tags.length || tags.includes('job')) && <JobsList jobs={jobs} />}
            {users.length > 0 && canManageMembers && (!tags.length || tags.includes('user')) && (
              <UsersList users={users} />
            )}
            {workspaces.length > 0 && (!tags.length || tags.includes('workspace')) && (
              <WorkspacesList workspaces={workspaces} />
            )}
          </>
        ) : null}
      </div>
    </Wrapper>
  );
};
